import type { ModelAssetNode } from '../assets/model-asset-types'
import { buildProjectModelDesignTreeProjection } from '../design-tree/build-project-model-design-tree-projection'
import { ProjectModelRuntimeStateResolver } from '../runtime-state/ProjectModelRuntimeStateResolver'
import type { ProjectModelLoadResult, ProjectModelSceneBinding } from './project-model-load-session'

/**
 * 删除项目模型加载结果节点的输入。
 */
export interface DeleteProjectModelLoadResultNodeInput {
  /**
   * 当前项目模型加载结果。
   */
  readonly loadResult: ProjectModelLoadResult
  /**
   * 要删除的节点 key。
   */
  readonly nodeKey: string
}

/**
 * 删除项目模型加载结果节点的差异。
 */
export interface DeleteProjectModelLoadResultNodeDiff {
  /**
   * 被删除节点及其全部后代 nodeKey。
   */
  readonly deletedNodeKeys: readonly string[]
  /**
   * 被过滤掉的场景绑定数量。
   */
  readonly filteredSceneBindingCount: number
  /**
   * 被过滤掉的装配文档 nodeKey。
   */
  readonly filteredAssemblyDocumentNodeKeys: readonly string[]
}

/**
 * 删除项目模型加载结果节点的输出。
 */
export interface DeleteProjectModelLoadResultNodeResult {
  /**
   * 裁剪后的项目模型加载结果。
   */
  readonly loadResult: ProjectModelLoadResult
  /**
   * 裁剪差异。
   */
  readonly diff: DeleteProjectModelLoadResultNodeDiff
}

/**
 * 收集模型资产子树中的全部 nodeKey。
 *
 * @param node 当前模型资产节点。
 * @param nodeKeys 输出集合。
 */
function collectModelAssetSubtreeNodeKeys(node: ModelAssetNode, nodeKeys: Set<string>): void {
  nodeKeys.add(node.nodeKey)
  node.children.forEach((child) => collectModelAssetSubtreeNodeKeys(child, nodeKeys))
}

/**
 * 从模型资产树中删除目标节点。
 *
 * @param node 当前节点。
 * @param targetNodeKey 要删除的节点 key。
 * @param deletedNodeKeys 已删除节点 key 集合。
 * @returns 删除后的当前节点；当前节点自身被删除时返回 null。
 */
function deleteModelAssetNodeRecursive(
  node: ModelAssetNode,
  targetNodeKey: string,
  deletedNodeKeys: Set<string>,
): ModelAssetNode | null {
  if (node.nodeKey === targetNodeKey) {
    collectModelAssetSubtreeNodeKeys(node, deletedNodeKeys)
    return null
  }

  const nextChildren: ModelAssetNode[] = []
  let didChildrenChange = false

  node.children.forEach((child) => {
    const nextChild = deleteModelAssetNodeRecursive(child, targetNodeKey, deletedNodeKeys)

    if (nextChild) {
      nextChildren.push(nextChild)
    } else {
      didChildrenChange = true
    }
    if (nextChild !== child) {
      didChildrenChange = true
    }
  })

  if (!didChildrenChange) {
    return node
  }

  return {
    ...node,
    children: nextChildren,
  }
}

/**
 * 收集当前模型树仍可达的 nodeKey。
 *
 * @param node 当前模型资产节点。
 * @param nodeKeys 输出集合。
 */
function collectReachableNodeKeys(node: ModelAssetNode, nodeKeys: Set<string>): void {
  nodeKeys.add(node.nodeKey)
  node.children.forEach((child) => collectReachableNodeKeys(child, nodeKeys))
}

/**
 * 判断场景绑定是否仍属于裁剪后的模型树。
 *
 * @param binding 场景绑定。
 * @param reachableNodeKeys 可达 nodeKey 集合。
 * @returns 仍可达时返回 true。
 */
function isReachableSceneBinding(
  binding: ProjectModelSceneBinding,
  reachableNodeKeys: ReadonlySet<string>,
): boolean {
  return reachableNodeKeys.has(binding.nodeKey)
}

/**
 * 从项目模型加载结果中删除一个节点及其子树。
 *
 * 设计说明：该函数只处理内存模型投影、设计树投影、sceneBindings 与装配文档索引，不负责
 * three.js 资源释放。调用方应继续通过加载器事务提交结果，由现有场景替换流程释放旧资源。
 *
 * @param input 删除输入。
 * @returns 裁剪后的加载结果和差异。
 * @throws 当目标 nodeKey 为空、指向项目根节点或无法在加载结果中找到时。
 */
export function deleteProjectModelLoadResultNode(
  input: DeleteProjectModelLoadResultNodeInput,
): DeleteProjectModelLoadResultNodeResult {
  const targetNodeKey = input.nodeKey.trim()

  if (!targetNodeKey) {
    throw new Error('project model delete nodeKey missing')
  }
  if (targetNodeKey === input.loadResult.root.nodeKey) {
    throw new Error('project model delete cannot remove root')
  }

  const deletedNodeKeys = new Set<string>()
  const root = deleteModelAssetNodeRecursive(input.loadResult.root, targetNodeKey, deletedNodeKeys)

  if (!root || deletedNodeKeys.size === 0) {
    throw new Error(`project model delete node not found: ${targetNodeKey}`)
  }

  const reachableNodeKeys = new Set<string>()
  collectReachableNodeKeys(root, reachableNodeKeys)

  const sceneBindings = input.loadResult.sceneBindings.filter((binding) => (
    isReachableSceneBinding(binding, reachableNodeKeys)
  ))
  const retainedRuntimeNodeKeys = new Set(
    Array.from(input.loadResult.runtimeState.bindingsByNodeKey.keys())
      .filter(nodeKey => !deletedNodeKeys.has(nodeKey)),
  )
  const runtimeState = new ProjectModelRuntimeStateResolver().retain(
    input.loadResult.runtimeState,
    retainedRuntimeNodeKeys,
  )
  const assemblyDocumentsByNodeKey = new Map(input.loadResult.assemblyDocumentsByNodeKey)
  const filteredAssemblyDocumentNodeKeys: string[] = []

  for (const deletedNodeKey of deletedNodeKeys) {
    if (assemblyDocumentsByNodeKey.delete(deletedNodeKey)) {
      filteredAssemblyDocumentNodeKeys.push(deletedNodeKey)
    }
  }

  return {
    loadResult: {
      ...input.loadResult,
      root,
      designTree: buildProjectModelDesignTreeProjection({
        root,
        sceneBindings,
      }),
      sceneBindings,
      runtimeState,
      assemblyDocumentsByNodeKey,
    },
    diff: {
      deletedNodeKeys: Array.from(deletedNodeKeys),
      filteredSceneBindingCount: input.loadResult.sceneBindings.length - sceneBindings.length,
      filteredAssemblyDocumentNodeKeys,
    },
  }
}
